import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { Link, useNavigate } from 'react-router';
import type { PublicCatalogue } from '@ebu/contracts';
import { Button, Icon, Notice, commandForKey } from '@ebu/ui';
import { PageHeading, useDocumentTitle } from '../../app/page';
import { detectWebGl2 } from '../../diagnostics/capabilities';
import { useDiagnostics } from '../../diagnostics/context';
import { useI18n } from '../../i18n/i18n';
import { CatalogueGate } from '../catalogue/CatalogueGate';
import { useGuest } from '../guest/guest-state';
import { PLACE_MODULE, offers } from '../guest/offers';
import { PlacePicture } from '../kids/pictures';
import { ReadAloudButton, speak } from '../kids/read-aloud';
import { WhoIsPlaying } from '../kids/WhoIsPlaying';
import type { WorldHandle } from '../world/scene';
import {
  HUB_PLACES,
  getPlace,
  isPlaceId,
  movePlaceFocus,
  type HubPlace,
  type PlaceId,
} from './hub-model';
import { IslandMap } from './IslandMap';
import { PosterArt } from './PosterArt';
import { posterFor, type PosterSpec } from './poster-art';

/**
 * Entrance at "/": the Kids World town (ADR 0009). A walkable 3D town (PlayCanvas, lazy) where each
 * district is a part of Kids World; a simple 2D map shows the same places when WebGL2 is missing,
 * the 3D view fails, or the visitor picks the simple map. The destination bar is the accessible
 * place list (one roving Tab stop, arrow keys via the central input-command table, Enter goes in);
 * walking the explorer to a district's door and pressing Enter also goes in. The Cinema card shows
 * the broadcaster's rights-checked catalogue immediately.
 *
 * ADR 0017: the welcome is "Who is playing?"; the destinations are big picture buttons (pictures
 * only for little ones, with read-aloud), and going into a place flies the camera there first.
 */
export function HubPage() {
  const { t, errorMessage, locale } = useI18n();
  useDocumentTitle(t('hub.title'));
  const navigate = useNavigate();
  const logger = useDiagnostics();
  const {
    graphics,
    setGraphics,
    broadcaster,
    catalogue,
    welcomeSeen,
    dismissWelcome,
    ageMode,
    setAgeMode,
    storage,
  } = useGuest();
  const offered = (p: HubPlace) => {
    const module = PLACE_MODULE[p.id];
    return module === undefined || offers(broadcaster, module);
  };
  const [focused, setFocused] = useState<PlaceId>('cinema');
  const [atDoor, setAtDoor] = useState<PlaceId | null>(null);
  // True while the explorer is walking: choosing the current place then still moves the camera.
  const [exploring, setExploring] = useState(false);
  const webgl2 = useMemo(() => detectWebGl2(), []);
  const [failure, setFailure] = useState<string | null>(webgl2 ? null : 'webgl2-missing');
  const mode = graphics === 'world' && webgl2 && failure === null ? 'world' : 'map';
  const buttons = useRef(new Map<PlaceId, HTMLButtonElement>());
  const labels = useRef(new Map<string, HTMLElement>());
  const list = useRef<HTMLUListElement>(null);
  const world = useRef<WorldHandle | null>(null);
  // Until the 3D scene positions the name labels, they sit at their map positions.
  const [sceneReady, setSceneReady] = useState(false);
  const mapLayout = mode === 'map' || !sceneReady;
  // "Who is playing?" until it has been answered on this browser (ADR 0017).
  const welcome = !welcomeSeen && ageMode === null && storage !== 'pending';

  const select = (id: PlaceId, moveFocus = false) => {
    setFocused(id);
    setAtDoor(null);
    setExploring(false);
    world.current?.setView(getPlace(id).view);
    if (moveFocus) buttons.current.get(id)?.focus();
  };
  // In the 3D town the camera flies to the place first, then you are inside (ADR 0017).
  const leaving = useRef(0);
  useEffect(() => () => window.clearTimeout(leaving.current), []);
  const enter = (place: HubPlace) => {
    if (mode === 'world' && world.current) {
      world.current.setView(place.view);
      window.clearTimeout(leaving.current);
      leaving.current = window.setTimeout(() => void navigate(place.route), 900);
    } else void navigate(place.route);
  };

  // Arrow keys / Home / End move between places while focus is on the destination bar.
  useEffect(() => {
    const el = list.current;
    if (!el) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const command = commandForKey(event);
      if (!command || command === 'activate' || command === 'back') return;
      event.preventDefault();
      const next = movePlaceFocus(focused, command);
      setFocused(next);
      setAtDoor(null);
      world.current?.setView(getPlace(next).view);
      buttons.current.get(next)?.focus();
    };
    el.addEventListener('keydown', onKeyDown);
    return () => el.removeEventListener('keydown', onKeyDown);
  }, [focused]);

  // A browser without WebGL2 is reported once, as a coded warning, and gets the map.
  useEffect(() => {
    if (webgl2) return;
    logger.warn('hub', '3D town unavailable; showing the simple map', {
      code: 'WORLD-RENDER-001',
      origin: 'apps/web/src/features/hub/HubPage.tsx:HubPage',
      context: { reason: 'webgl2-missing' },
    });
  }, [webgl2, logger]);

  const start = (little: boolean) => {
    dismissWelcome();
    if (little) {
      select('cinema', true);
      speak(t('kids.town.say'), locale, true);
    } else if (mode === 'world' && world.current) {
      world.current.explore();
      setExploring(true);
    } else {
      select('cinema', true);
    }
  };

  const onRenderFailure = (reason: string, error: unknown) => {
    logger.warn('hub', '3D town unavailable; showing the simple map', {
      code: 'WORLD-RENDER-001',
      origin: 'apps/web/src/features/hub/HubPage.tsx:WorldStage',
      context: { reason, err: error },
    });
    setFailure(reason);
  };

  const place = getPlace(focused);
  const items = catalogue.status === 'ready' ? catalogue.data.items : null;
  // The big cinema screen shows a programme you can actually watch, when there is one.
  const posters = useMemo(() => {
    const list = items ?? [];
    const playable = list.filter((i) => i.media !== null);
    return [...playable, ...list.filter((i) => i.media === null)].slice(0, 3).map(posterFor);
  }, [items]);

  return (
    <div className="hub" data-mode={mode} data-testid="hub">
      <PageHeading hidden>{t('hub.title')}</PageHeading>
      <div className="hub__stage">
        {mode === 'world' ? (
          <WorldStage
            worldRef={world}
            welcome={welcome}
            accent={broadcaster?.theme.accent ?? '#1d3036'}
            posters={posters}
            labels={labels}
            onPick={(id) => {
              if (isPlaceId(id)) select(id);
            }}
            onNear={(id) => {
              if (id && isPlaceId(id)) {
                setFocused(id);
                setAtDoor(id);
              } else setAtDoor(null);
            }}
            onEnter={(id) => {
              if (isPlaceId(id)) enter(getPlace(id));
            }}
            onFailure={onRenderFailure}
            onReady={() => setSceneReady(true)}
          />
        ) : (
          <IslandMap focused={focused} />
        )}
        {/* Name labels float over the buildings (or sit on the map). Mouse shortcuts only: the
            destination bar below is the accessible list of the same places. */}
        <div className="hub__labels" aria-hidden="true">
          {HUB_PLACES.map((p) => (
            <button
              key={p.id}
              type="button"
              tabIndex={-1}
              ref={(el) => {
                if (el) labels.current.set(p.id, el);
                else labels.current.delete(p.id);
              }}
              className={`place-label place-label--${p.tone}`}
              data-current={p.id === focused}
              style={
                mapLayout
                  ? {
                      left: `${p.map.left}%`,
                      top: `${p.map.top}%`,
                      transform: 'translate(-50%, -50%)',
                      visibility: 'visible',
                    }
                  : undefined
              }
              onClick={() => enter(p)}
            >
              <Icon name={p.icon} size={20} />
              <span>{t(p.name)}</span>
              {p.status === 'building' && (
                <span className="place-label__badge">{t('hub.building')}</span>
              )}
              {!offered(p) && <span className="place-label__badge">{t('hub.notOffered')}</span>}
            </button>
          ))}
        </div>
        {failure && graphics === 'world' && (
          <div className="hub__notice">
            <Notice tone="warning">
              <p>{errorMessage('WORLD-RENDER-001')}</p>
            </Notice>
          </div>
        )}
        {welcome && (
          <div className="hub__welcome">
            <WhoIsPlaying
              onChoose={(chosen) => {
                setAgeMode(chosen);
                start(chosen === 'little');
              }}
            />
          </div>
        )}
      </div>

      <PlaceCard
        place={place}
        atDoor={atDoor === place.id}
        onEnter={() => enter(place)}
        mode={mode}
        canUse3d={webgl2 && failure === null}
        onToggleMode={() => {
          setSceneReady(false);
          setGraphics(mode === 'world' ? 'map' : 'world');
        }}
      />

      <nav className="destinations" aria-label={t('hub.placesLabel')}>
        <ReadAloudButton text={t('kids.town.say')} slow className="read-aloud destinations__read" />
        <ul ref={list} aria-label={t('hub.placesLabel')}>
          {HUB_PLACES.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                ref={(el) => {
                  if (el) buttons.current.set(p.id, el);
                  else buttons.current.delete(p.id);
                }}
                className={`destination destination--${p.tone}`}
                data-place={p.id}
                tabIndex={p.id === focused ? 0 : -1}
                aria-current={p.id === focused ? 'location' : undefined}
                aria-describedby={`place-note-${p.id}`}
                onFocus={() => {
                  if (p.id !== focused || exploring) select(p.id);
                }}
                onMouseEnter={() => {
                  if (p.id !== focused || exploring) select(p.id);
                }}
                onClick={() => enter(p)}
              >
                <span className="destination__icon" aria-hidden="true">
                  <Icon name={p.icon} size={22} />
                </span>
                <span className="destination__picture" aria-hidden="true">
                  <PlacePicture place={p.id} />
                </span>
                <span className="destination__name">{t(p.name)}</span>
                {p.status === 'building' && (
                  <span className="place-label__badge">{t('hub.building')}</span>
                )}
                {!offered(p) && <span className="place-label__badge">{t('hub.notOffered')}</span>}
                <span className="ui-visually-hidden" id={`place-note-${p.id}`}>
                  {t(p.description)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}

function PlaceCard({
  place,
  atDoor,
  onEnter,
  mode,
  canUse3d,
  onToggleMode,
}: {
  place: HubPlace;
  atDoor: boolean;
  onEnter: () => void;
  mode: 'world' | 'map';
  canUse3d: boolean;
  onToggleMode: () => void;
}) {
  const { t } = useI18n();
  const { broadcaster } = useGuest();
  const building = place.status === 'building';
  const module = PLACE_MODULE[place.id];
  const notOffered = module !== undefined && !offers(broadcaster, module);
  return (
    <section
      className={`place-card place-card--${place.tone}`}
      aria-labelledby="place-card-title"
      data-testid="place-card"
    >
      <span className="eyebrow">
        <span className="eyebrow__dot" aria-hidden="true" />
        {building ? t('hub.building') : t('hub.title')}
      </span>
      <h2 id="place-card-title" className="place-card__title">
        <Icon name={place.icon} size={28} />
        {t(place.name)}
      </h2>
      <p className="place-card__note">
        {notOffered && broadcaster
          ? t('offer.title', { place: t(place.name), broadcaster: broadcaster.displayName })
          : t(place.description)}
      </p>
      {place.id === 'cinema' && (
        <CatalogueGate>{(catalogue) => <NowShowing catalogue={catalogue} />}</CatalogueGate>
      )}
      <p className="place-card__door" aria-live="polite">
        {atDoor ? t('hub.pressEnter') : ''}
      </p>
      <div className="button-row">
        <Button onClick={onEnter} className="ui-button--large">
          {building ? t('hub.lookInside') : t('hub.goIn')}
        </Button>
        {(canUse3d || mode === 'world') && (
          <Button variant="secondary" onClick={onToggleMode}>
            {mode === 'world' ? t('hub.useMap') : t('hub.use3d')}
          </Button>
        )}
      </div>
      <p className="place-card__hint">
        {mode === 'world' ? `${t('hub.keysHint')} ${t('hub.walkHint')}` : t('hub.keysHint')}
      </p>
    </section>
  );
}

function NowShowing({ catalogue }: { catalogue: PublicCatalogue }) {
  const { t } = useI18n();
  const items = catalogue.items.slice(0, 3);
  if (items.length === 0) return <p>{t('discover.noItems')}</p>;
  return (
    <div className="now-showing" data-testid="now-showing">
      <h3 className="now-showing__title">{t('hub.nowShowing')}</h3>
      <ul className="now-showing__list">
        {items.map((item) => (
          <li key={item.id}>
            <Link to={`/programme/${item.id}`} className="poster-link">
              <PosterArt item={item} sampleLabel={t('poster.sample')} showTitle={false} />
              <span className="poster-link__title" lang={item.textLocale}>
                {item.title}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Owns the canvas and the lazily imported world. Any failure (engine chunk not loading, scene setup
 * error) is reported once and the page falls back to the map.
 */
function WorldStage({
  worldRef,
  welcome,
  accent,
  posters,
  labels,
  onPick,
  onNear,
  onEnter,
  onFailure,
  onReady,
}: {
  worldRef: RefObject<WorldHandle | null>;
  welcome: boolean;
  accent: string;
  posters: PosterSpec[];
  labels: RefObject<Map<string, HTMLElement>>;
  onPick: (id: string) => void;
  onNear: (id: string | null) => void;
  onEnter: (id: string) => void;
  onFailure: (reason: string, error: unknown) => void;
  onReady: () => void;
}) {
  const { t } = useI18n();
  const canvas = useRef<HTMLCanvasElement>(null);
  const [ready, setReady] = useState(false);
  const latest = useRef({ welcome, accent, posters, onPick, onNear, onEnter, onFailure, onReady });
  useEffect(() => {
    latest.current = { welcome, accent, posters, onPick, onNear, onEnter, onFailure, onReady };
  });

  useEffect(() => {
    let cancelled = false;
    const el = canvas.current;
    if (!el) return;
    import('../world/scene').then(
      ({ mountWorld }) => {
        if (cancelled) return;
        try {
          worldRef.current = mountWorld({
            canvas: el,
            view: latest.current.welcome ? 'overview' : 'arrival',
            interactive: true,
            accent: latest.current.accent,
            posters: latest.current.posters,
            anchors: HUB_PLACES,
            labels: labels.current,
            onReady: () => {
              setReady(true);
              latest.current.onReady();
            },
            onPick: (id) => latest.current.onPick(id),
            onNear: (id) => latest.current.onNear(id),
            onEnter: (id) => latest.current.onEnter(id),
          });
        } catch (error) {
          latest.current.onFailure('scene-init', error);
        }
      },
      (error: unknown) => {
        if (!cancelled) latest.current.onFailure('engine-load', error);
      },
    );
    return () => {
      cancelled = true;
      worldRef.current?.destroy();
      worldRef.current = null;
    };
  }, [labels, worldRef]);

  useEffect(() => worldRef.current?.setAccent(accent), [accent, ready, worldRef]);
  useEffect(() => worldRef.current?.setPosters(posters), [posters, ready, worldRef]);

  return (
    <>
      <canvas
        ref={canvas}
        className="hub__canvas"
        data-testid="island-canvas"
        tabIndex={0}
        aria-label={t('hub.canvasLabel')}
      />
      {!ready && (
        <p className="hub__loading" role="status">
          {t('hub.loading3d')}
        </p>
      )}
    </>
  );
}
